// PCHabitabilidadTemplate.ts
// Genera el HTML del Dictamen de Habitabilidad a partir de variables.
// Basado en el HTML provisto por el usuario.

export interface DictamenHabitabilidadVars {
  // Encabezado
  dictamenFolio: string;             // ej: "DH/PCT/001"
  area: string;                      // ej: "coordinación de proteccion civil"
  fechaLugar: string;                // ej: "TOTOLAC, TLAX; AGOSTO 01 DE 2025"

  // Destinatario
  destinatarioLinea: string;         // línea completa en mayúsculas (ej: "C. JUAN SANDOVAL ORTIZ Y ESPOSA LAURA GARCIA TEMOLTZIN")
  destinatarioPresente?: string;     // por defecto "presente"

  // Título
  titulo?: string;                   // por defecto: "dictamen de habitabildad" (se respeta grafía del original)

  // Variables en NEGRITAS del cuerpo del texto
  solicitante: string;               // <b>C. Juan Sandoval Ortiz y esposa Laura García Temoltzin</b>
  predioDenominado: string;          // <b>Huexintla</b>
  ubicacionDireccion: string;        // <b>Privada de la Presa #13 de la comunidad de San Juan</b>
  municipio: string;                 // <b>Municipio de Totolac; Tlaxcala.</b>
  fechaSupervision: string;          // "31 de julio del año 2025" (no estaba en negritas, pero es param)
  riesgoTexto: string;               // <b>NO</b> (o "SÍ", "NO", etc.)
  funcionamientoTexto: string;       // <b>SEGURO Y BUEN</b>

  // Firmas
  firma1Nombre: string;
  firma1Cargo: string;
  firma2Nombre: string;
  firma2Cargo: string;

  // Opcional
  autoPrint?: boolean;               // si true, agrega script para window.print()
}

export function renderPCHabitabilidad(v: DictamenHabitabilidadVars): string {
  const titulo = v.titulo ?? 'dictamen de habitabildad';
  const presente = v.destinatarioPresente ?? 'presente';

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Dictamen de Habitabilidad</title>
  <style>
    @media print{
      html, body { position: relative; margin: 0; padding: 0; height: auto !important; min-height: auto !important; }
      section, div { page-break-inside: avoid; }
    }
    body {
      position: relative;
      width: 21cm;
      height: 27.9cm; /* Carta */
      margin: 0;
      font-family: Arial, sans-serif;
      font-size: 12pt;
      page-break-inside: avoid;
      margin-bottom: 0;
    }
    .remitente{
      text-align: right;
      margin-top: 50px;
      margin-right: 50px;
      font-size: 13pt;
      font-weight: bold;
      text-transform: uppercase;
    }
    .destinatario{
      text-align: left;
      margin-top: 50px;
      margin-left: 50px;
      max-width: 350px;
      font-size: 13pt;
      font-weight: bold;
      text-transform: uppercase;
    }
    .cuerpo{
      text-align: justify;
      margin-right: 50px;
      margin-left: 50px;
      margin-bottom: 50px;
      min-height: 300px;
    }
    .titulo{
      font-size: 16pt;
      font-weight: bold;
      text-align: center;
      margin-top: 20px;
      text-transform: uppercase;
    }
    .atento{ margin-top: 50px; text-align: center; }
    .firmas{
      margin-top: 100px;
      margin-right: 200px;
      margin-left: 50px;
      display: flex;
      gap: 275px;
      justify-content: space-between;
      align-items: center;
      text-transform: uppercase;
    }
    .firma1, .firma2{
      border-top: solid 1px;
      text-align: center;
      min-width: 200px;
    }
  </style>
</head>
<body>
  <section class="remitente">
    <div>DICTAMEN: ${escapeHtml(v.dictamenFolio)}</div>
    <div>Area: ${escapeHtml(v.area)}</div>
    <div>${escapeHtml(v.fechaLugar)}</div>
  </section>

  <section class="destinatario">
    <div>${escapeHtml(v.destinatarioLinea)}</div>
    <div>${escapeHtml(presente)}</div>
  </section>

  <section class="cuerpo">
    <div class="titulo">${escapeHtml(titulo)}</div>
    <div>
      Basándose en la supervisión ocular realizada por la Coordinación de Protección Civil Municipal el día ${escapeHtml(v.fechaSupervision)},
      por medio del personal autorizado y a solicitud del <b>${escapeHtml(v.solicitante)}</b> con el predio denominado
      <b>${escapeHtml(v.predioDenominado)}</b> ubicado en <b>${escapeHtml(v.ubicacionDireccion)}</b> perteneciente al
      <b>${escapeHtml(v.municipio)}</b> Con finalidad de verificar las condiciones de habitabilidad del inmueble ubicado en dicho
      domicilio, con fundamento en lo establecido en la Ley General de Protección Civil, la Ley de Protección Civil para el Estado de
      Tlaxcala y el Reglamento de la Ley de Protección Civil para el Municipio de Totolac; se verifico que por simple apreciación visual
      el inmueble <b>${escapeHtml(v.riesgoTexto)}</b> presenta riesgo y por lo consiguiente, su <b>${escapeHtml(v.funcionamientoTexto)}</b>
      funcionamiento.
    </div>
  </section>

  <div class="atento">Atentamente</div>

  <section class="firmas">
    <div class="firma1">
      <div>${escapeHtml(v.firma1Nombre)}</div>
      <div>${escapeHtml(v.firma1Cargo)}</div>
    </div>
    <div class="firma2">
      <div>${escapeHtml(v.firma2Nombre)}</div>
      <div>${escapeHtml(v.firma2Cargo)}</div>
    </div>
  </section>

  ${v.autoPrint ? `<script>window.onload = function(){ window.print(); };</script>` : ''}
</body>
</html>`;
}

// Pequeño escape para evitar inyección cuando interpolamos variables.
function escapeHtml(input: string): string {
  return String(input)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}
